// Single source of truth for API base URL + small fetch / time helpers.
// Components should import from here instead of hand-rolling fetch + Date
// parsing each time.

export const API_URL = import.meta.env.VITE_API_URL ?? "";

// pipe-agent base URL for endpoints the web hits directly (currently /dial).
// Wrangler dev's worker-to-localhost fetch is flaky, so we bypass the API
// proxy for this one path. Default matches the bot's local dev port.
export const BOT_URL = import.meta.env.VITE_BOT_URL ?? "http://localhost:8080";

/** Build a full API URL: `apiUrl("/calls", { limit: 50 })`. */
export function apiUrl(path: string, params?: Record<string, string | number | undefined>): string {
  const base = `${API_URL}${path.startsWith("/") ? path : `/${path}`}`;
  if (!params) return base;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${base}?${s}` : base;
}

/** Thin fetch wrapper that throws on non-2xx and parses JSON. */
export async function apiFetch<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await fetch(input.startsWith("http") ? input : `${API_URL}${input}`, init);
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new ApiError(`HTTP ${res.status}`, res.status, detail);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public detail: string) {
    super(message);
    this.name = "ApiError";
  }
}

/** Open a typed EventSource. Caller is responsible for cleanup. */
export function openEventSource(path: string): EventSource {
  return new EventSource(`${API_URL}${path}`);
}

// ---------------------------------------------------------------------------
// Timestamp helpers
//
// D1's `datetime('now')` returns "YYYY-MM-DD HH:MM:SS" in UTC without a
// timezone marker. Browsers parse that as local time, which makes timestamps
// drift. `parseTs` normalizes everything to UTC. Callers stop having to write
// `iso.endsWith("Z") ? iso : iso + "Z"` in every component.
// ---------------------------------------------------------------------------
export function parseTs(ts: string): Date {
  const hasTz = ts.endsWith("Z") || /[+-]\d\d:?\d\d$/.test(ts);
  // SQLite "YYYY-MM-DD HH:MM:SS" → ISO "YYYY-MM-DDTHH:MM:SSZ"
  const iso = hasTz ? ts : `${ts.replace(" ", "T")}Z`;
  return new Date(iso);
}

/**
 * Format a phone number for display. We only style E.164 +1 NANP numbers
 * — anything else is returned as-is so we don't mangle international
 * numbers we don't have a layout for.
 *   "+15555550101" → "+1 555 555 0101"
 */
export function fmtPhone(raw: string | null | undefined): string {
  if (!raw) return "";
  const m = raw.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 ${m[1]} ${m[2]} ${m[3]}`;
  return raw;
}

export function fmtTime(ts: string): string {
  return parseTs(ts).toLocaleTimeString();
}

export function fmtDateTime(ts: string): string {
  return parseTs(ts).toLocaleString();
}

export function relTime(ts: string): string {
  const d = parseTs(ts);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  // Clamp future-dated timestamps (clock skew, seeded data, etc.) to
  // "just now" instead of leaking a negative number into the UI.
  if (secs < 0) return "just now";
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return d.toLocaleDateString();
}

/** Human-readable call duration. Returns "ongoing" while the call is live. */
export function fmtDuration(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return "ongoing";
  const start = parseTs(startedAt).getTime();
  const end = parseTs(endedAt).getTime();
  let secs = Math.max(0, Math.floor((end - start) / 1000));
  const h = Math.floor(secs / 3600);
  secs -= h * 3600;
  const m = Math.floor(secs / 60);
  const s = secs - m * 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/**
 * Click handler for `<Link>` rows that wrap selectable text. If the user
 * has just released the mouse after dragging out a text selection, we
 * suppress the row's navigation so they can copy. Plain clicks (no
 * selection) still navigate as expected.
 *
 * Why this exists: every mailbox row (Inbound / Outbound / Co-pilot /
 * Scenarios) is a single `<Link>` wrapping the entire row, including
 * the phone number and customer name. Operators want to copy those —
 * but a click that ends a selection inside an `<a>` would still fire
 * the link's navigation.
 */
export function suppressNavOnSelection(e: import("react").MouseEvent): void {
  const sel = window.getSelection();
  if (sel && sel.toString().length > 0) {
    e.preventDefault();
    e.stopPropagation();
  }
}

/** Pretty-print a JSON-stringified blob; returns the raw string on failure. */
export function prettyJson(s: string | null | undefined): string {
  if (!s) return "";
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
